// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import { getDatabase, ref, get } from 'firebase/database';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userData, setUserData] = useState(null);

    const db = getDatabase();

    const resetAuthState = () => {
        setUser(null);
        setUserData(null);
        setError(null);
    };

    const login = async (email, password) => {
        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            setUser(userCredential.user);
            await fetchUserData(userCredential.user.uid);
        } catch (error) {
            setError(error.message);
            throw error;
        }
    };

    const fetchUserData = async (uid) => {
        const userRef = ref(db, `users/${uid}`);
        try {
            const snapshot = await get(userRef);
            if (snapshot.exists()) {
                setUserData({ ...snapshot.val(), uid });
            } else {
                resetAuthState();
            }
        } catch (error) {

            resetAuthState();
        }
    };

    const logout = async () => {
        try {
            await signOut(auth);
            resetAuthState();
        } catch (error) {
            setError(error.message);
        }
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            setUser(currentUser);
            if (currentUser) {
                await fetchUserData(currentUser.uid);
            } else {
                resetAuthState();
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const value = {
        user,
        userData,
        firstName: userData?.firstName,
        position: userData?.position,
        role: userData?.role,
        login,
        logout,
        error,
        loading,
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
